import { useEffect, useMemo, useState } from 'react';
import type { ComponentType, CSSProperties } from 'react';
import { FiChevronRight, FiFolder } from 'react-icons/fi';

import { isScopePathIncluded } from '../workflowShared';
import {
  runFormatNumber,
  runGetScopeAncestorPaths,
  runGetScopeParentPath,
  type GitHubLoadState,
  type GitHubRepositoryDirectorySummary
} from './helpers/runScreenTypes';

type RunScreenIconComponent = ComponentType<{ 'aria-hidden'?: boolean }>;

const IconChevronRight = FiChevronRight as unknown as RunScreenIconComponent;
const IconFolder = FiFolder as unknown as RunScreenIconComponent;

function RunScopeTreeChevron({ expanded }: { expanded: boolean }) {
  return (
    <span className={`scope-tree-chevron ${expanded ? 'expanded' : ''}`} aria-hidden={true}>
      <IconChevronRight aria-hidden={true} />
    </span>
  );
}

export function RunScopeFolderTree({
  githubTreeState,
  githubTreeError,
  directories,
  scopePaths,
  onSelectRepositoryRoot,
  onToggleScopePath,
  readOnly = false,
  rootLabel = 'Repository root'
}: {
  githubTreeState: GitHubLoadState;
  githubTreeError: string;
  directories: GitHubRepositoryDirectorySummary[];
  scopePaths: string[];
  onSelectRepositoryRoot?: () => void;
  onToggleScopePath?: (scopePath: string) => void;
  /** Only browses the folders, for codebases that are always tested in full. */
  readOnly?: boolean;
  rootLabel?: string;
}) {
  const [isRootExpanded, setIsRootExpanded] = useState(true);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => new Set());
  const isRootSelected = !readOnly && scopePaths.length === 0;
  const selectedFolderCount = isRootSelected ? directories.length : scopePaths.length;
  const directoryPathSet = useMemo(() => {
    return new Set(directories.map((directory) => directory.path));
  }, [directories]);
  const directoryPathsWithChildren = useMemo(() => {
    const pathsWithChildren = new Set<string>();

    directories.forEach((directory) => {
      const parentPath = runGetScopeParentPath(directory.path);

      if (parentPath && directoryPathSet.has(parentPath)) {
        pathsWithChildren.add(parentPath);
      }
    });

    return pathsWithChildren;
  }, [directories, directoryPathSet]);
  const visibleDirectories = useMemo(() => {
    if (!isRootExpanded) {
      return [];
    }

    return directories.filter((directory) => {
      return runGetScopeAncestorPaths(directory.path).every((ancestorPath) => expandedPaths.has(ancestorPath));
    });
  }, [directories, expandedPaths, isRootExpanded]);

  useEffect(() => {
    setIsRootExpanded(true);
    setExpandedPaths((currentExpandedPaths) => {
      const nextExpandedPaths = new Set<string>();

      currentExpandedPaths.forEach((expandedPath) => {
        if (directoryPathSet.has(expandedPath)) {
          nextExpandedPaths.add(expandedPath);
        }
      });

      return nextExpandedPaths;
    });
  }, [directoryPathSet]);

  useEffect(() => {
    if (scopePaths.length === 0) {
      return;
    }

    setIsRootExpanded(true);
    setExpandedPaths((currentExpandedPaths) => {
      const nextExpandedPaths = new Set(currentExpandedPaths);

      scopePaths.forEach((scopePath) => {
        runGetScopeAncestorPaths(scopePath).forEach((ancestorPath) => {
          if (directoryPathSet.has(ancestorPath)) {
            nextExpandedPaths.add(ancestorPath);
          }
        });
      });

      return nextExpandedPaths;
    });
  }, [scopePaths, directoryPathSet]);

  const toggleDirectoryExpansion = (scopePath: string) => {
    setExpandedPaths((currentExpandedPaths) => {
      const nextExpandedPaths = new Set(currentExpandedPaths);

      if (nextExpandedPaths.has(scopePath)) {
        nextExpandedPaths.delete(scopePath);
      } else {
        nextExpandedPaths.add(scopePath);
      }

      return nextExpandedPaths;
    });
  };

  return (
    <div className="repository-scope-picker">
      <div className="scope-folders-heading">
        <span>Folders</span>
        {!readOnly && (
          <div>
            <button type="button" onClick={onSelectRepositoryRoot}>Select all</button>
            <span aria-hidden={true} />
            <button type="button" onClick={onSelectRepositoryRoot}>Clear</button>
          </div>
        )}
      </div>
      <div className="scope-tree" role="tree" aria-label="Repository folders">
        <div
          role="treeitem"
          aria-level={1}
          aria-expanded={directories.length > 0 ? isRootExpanded : undefined}
          aria-selected={readOnly ? undefined : isRootSelected}
          className={`scope-tree-row root ${isRootSelected ? 'selected' : ''}`}
        >
          <button
            type="button"
            className={`scope-tree-disclosure ${directories.length === 0 ? 'placeholder' : ''}`}
            aria-label={isRootExpanded ? 'Collapse repository root' : 'Expand repository root'}
            onClick={() => setIsRootExpanded((expanded) => !expanded)}
            disabled={directories.length === 0}
          >
            <RunScopeTreeChevron expanded={isRootExpanded} />
          </button>
          {readOnly ? (
            <span className="scope-tree-select static">
              <IconFolder aria-hidden={true} />
              <span className="scope-tree-name">{rootLabel}</span>
              <span className="scope-tree-meta">{runFormatNumber(directories.length)} folders</span>
            </span>
          ) : (
            <button type="button" className="scope-tree-select" onClick={onSelectRepositoryRoot}>
              <span className={`scope-tree-check ${isRootSelected ? 'checked' : ''}`} aria-hidden={true} />
              <IconFolder aria-hidden={true} />
              <span className="scope-tree-name">{rootLabel}</span>
              <span className="scope-tree-meta">{directories.length} folders</span>
            </button>
          )}
        </div>

        {githubTreeState === 'loading' && (
          <p className="codebase-status scope-tree-status">Loading folders...</p>
        )}
        {githubTreeState === 'error' && (
          <p className="codebase-status error scope-tree-status">{githubTreeError}</p>
        )}
        {githubTreeState === 'ready' && directories.length === 0 && (
          <p className="codebase-status scope-tree-status">No folders found.</p>
        )}
        {githubTreeState === 'ready' && visibleDirectories.map((directory) => {
          const hasChildren = directoryPathsWithChildren.has(directory.path);
          const isExpanded = expandedPaths.has(directory.path);
          const isSelected = !readOnly && (isRootSelected || isScopePathIncluded(scopePaths, directory.path));
          const isExactSelection = !isRootSelected && scopePaths.includes(directory.path);

          return (
            <div
              role="treeitem"
              aria-level={directory.depth + 1}
              aria-expanded={hasChildren ? isExpanded : undefined}
              aria-selected={readOnly ? undefined : isSelected}
              className={`scope-tree-row ${isSelected ? 'selected' : ''} ${isExactSelection ? 'exact' : ''}`}
              key={directory.path}
              style={{ '--tree-depth': Math.max(0, directory.depth - 1) } as CSSProperties}
            >
              <button
                type="button"
                className={`scope-tree-disclosure ${hasChildren ? '' : 'placeholder'}`}
                aria-label={isExpanded ? `Collapse ${directory.name}` : `Expand ${directory.name}`}
                onClick={() => toggleDirectoryExpansion(directory.path)}
                disabled={!hasChildren}
              >
                <RunScopeTreeChevron expanded={isExpanded} />
              </button>
              {readOnly ? (
                <span className="scope-tree-select static">
                  <IconFolder aria-hidden={true} />
                  <span className="scope-tree-name">{directory.name}</span>
                </span>
              ) : (
                <button
                  type="button"
                  className="scope-tree-select"
                  onClick={() => onToggleScopePath?.(directory.path)}
                >
                  <span className={`scope-tree-check ${isSelected ? 'checked' : ''}`} aria-hidden={true} />
                  <IconFolder aria-hidden={true} />
                  <span className="scope-tree-name">{directory.name}</span>
                  <span className="scope-tree-meta">{isExactSelection ? 'selected' : isSelected ? 'included' : ''}</span>
                </button>
              )}
            </div>
          );
        })}
      </div>
      <span className="scope-folder-count">
        {readOnly || isRootSelected ? 'Full codebase' : `${runFormatNumber(selectedFolderCount)} selected`}
      </span>
    </div>
  );
}
